import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  X,
  Clock,
  Tag,
  CalendarBlank,
  CheckCircle,
  Circle,
  Trash,
  PencilSimple,
  ClockCounterClockwise,
  FunnelSimple,
  Plus,
  ListChecks,
  FloppyDisk,
  Copy,
  BookmarkSimple,
  Check,
  Lightning
} from '@phosphor-icons/react';
import { format, parseISO, isValid } from 'date-fns';
import TaskHistoryTimeline from '@/components/TaskHistoryTimeline';
import UpdateTaskModal from '@/components/UpdateTaskModal';
import EditTaskModal from '@/components/EditTaskModal';
import SaveTemplateModal from '@/components/SaveTemplateModal';
import TemplatePickerModal from '@/components/TemplatePickerModal';
import CopyFromTaskModal from '@/components/CopyFromTaskModal';
import SaveWorkflowTemplateModal from '@/components/SaveWorkflowTemplateModal';
import TeamComments from '@/components/TeamComments';
import TaskApproval from '@/components/TaskApproval';

const API = `${window.location.origin}/api`;

function SubtaskProgress({ subtasks }) {
  if (!subtasks || subtasks.length === 0) return null;
  const total = subtasks.length;
  const completed = subtasks.filter(s => s.status === 'completed' || s.completed).length;
  const percent = Math.round((completed / total) * 100);

  return (
    <div className="flex items-center gap-3" data-testid="subtask-progress">
      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-500 rounded-full transition-all duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-xs font-medium text-slate-500 whitespace-nowrap">
        {completed} of {total} done ({percent}%)
      </span>
    </div>
  );
}

function SubtaskChecklist({ taskId, subtasks: initialSubtasks, onUpdate }) {
  const [subtasks, setSubtasks] = useState(initialSubtasks || []);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showCopyFrom, setShowCopyFrom] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  useEffect(() => { setSubtasks(initialSubtasks || []); }, [initialSubtasks]);

  const toggleSubtask = async (subtaskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'open' : 'completed';
    try {
      const res = await axios.put(
        `${API}/tasks/${taskId}/subtasks/${subtaskId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      setSubtasks(res.data.subtasks || []);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to toggle subtask:', error);
    }
  };

  const addSubtask = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const res = await axios.post(
        `${API}/tasks/${taskId}/subtasks`,
        { title: newTitle.trim() },
        { withCredentials: true }
      );
      setSubtasks(res.data.subtasks || []);
      setNewTitle('');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to add subtask:', error);
    } finally {
      setAdding(false);
    }
  };

  const removeSubtask = async (subtaskId) => {
    try {
      const res = await axios.delete(
        `${API}/tasks/${taskId}/subtasks/${subtaskId}`,
        { withCredentials: true }
      );
      setSubtasks(res.data.subtasks || []);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to remove subtask:', error);
    }
  };

  const startEditSubtask = (st) => {
    setEditingId(st.id);
    setEditTitle(st.title);
  };

  const saveEditSubtask = async (subtaskId) => {
    if (!editTitle.trim()) { setEditingId(null); return; }
    try {
      const res = await axios.put(
        `${API}/tasks/${taskId}/subtasks/${subtaskId}`,
        { title: editTitle.trim() },
        { withCredentials: true }
      );
      setSubtasks(res.data.subtasks || []);
      setEditingId(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to edit subtask:', error);
    }
  };

  const handleTemplateApplied = (updatedTask) => {
    setSubtasks(updatedTask.subtasks || []);
    if (onUpdate) onUpdate();
  };

  const handleCopied = (updatedTask) => {
    setSubtasks(updatedTask.subtasks || []);
    if (onUpdate) onUpdate();
  };

  return (
    <div data-testid="subtask-checklist">
      {/* Progress bar */}
      <SubtaskProgress subtasks={subtasks} />

      {/* Template action buttons */}
      <div className="flex flex-wrap gap-1.5 mt-2 mb-1">
        {subtasks.length > 0 && (
          <button
            onClick={() => setShowSaveTemplate(true)}
            className="text-[11px] font-medium text-slate-500 bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
            data-testid="save-as-template-btn"
          >
            <FloppyDisk size={12} weight="bold" />
            Save as template
          </button>
        )}
        <button
          onClick={() => setShowTemplatePicker(true)}
          className="text-[11px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
          data-testid="use-template-btn"
        >
          <BookmarkSimple size={12} weight="bold" />
          Use template
        </button>
        <button
          onClick={() => setShowCopyFrom(true)}
          className="text-[11px] font-medium text-slate-500 bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
          data-testid="copy-from-task-btn"
        >
          <Copy size={12} weight="bold" />
          Copy from task
        </button>
      </div>

      {/* Checklist items */}
      <div className="mt-3 space-y-1">
        {subtasks.map((st) => {
          const isDone = st.status === 'completed' || st.completed;
          const stId = st.id || st.title;
          const isEditing = editingId === stId;
          return (
            <div
              key={stId}
              className="flex items-center gap-2 group py-1 px-1 -mx-1 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid={`subtask-item-${stId}`}
            >
              <button
                onClick={() => stId.startsWith('st_') ? toggleSubtask(stId, st.status || (st.completed ? 'completed' : 'open')) : null}
                className="flex-shrink-0"
                data-testid={`subtask-toggle-${stId}`}
              >
                {isDone ? (
                  <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                ) : (
                  <Circle size={18} weight="regular" className="text-slate-300 group-hover:text-navy transition-colors" />
                )}
              </button>
              {isEditing ? (
                <form
                  onSubmit={(e) => { e.preventDefault(); saveEditSubtask(stId); }}
                  className="flex-1 flex items-center gap-1"
                >
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    autoFocus
                    onBlur={() => saveEditSubtask(stId)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setEditingId(null); }}
                    className="flex-1 text-sm bg-white border border-blue-300 rounded-lg px-2 py-0.5 outline-none focus:ring-2 focus:ring-blue-100"
                    data-testid={`subtask-edit-input-${stId}`}
                  />
                </form>
              ) : (
                <span
                  className={`flex-1 text-sm cursor-pointer ${isDone ? 'line-through text-slate-400' : 'text-slate-800'}`}
                  onDoubleClick={() => stId.startsWith('st_') && startEditSubtask(st)}
                  title="Double-click to edit"
                >
                  {st.title}
                </span>
              )}
              {st.due_date && !isEditing && (
                <span className="text-xs text-slate-400">{st.due_date}</span>
              )}
              {stId.startsWith('st_') && !isEditing && (
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0">
                  <button
                    onClick={() => startEditSubtask(st)}
                    className="text-slate-300 hover:text-navy transition-colors p-0.5"
                    data-testid={`subtask-edit-${stId}`}
                    title="Edit sub-task"
                  >
                    <PencilSimple size={13} />
                  </button>
                  <button
                    onClick={() => removeSubtask(stId)}
                    className="text-slate-300 hover:text-red-500 transition-colors p-0.5"
                    data-testid={`subtask-remove-${stId}`}
                    title="Remove sub-task"
                  >
                    <X size={13} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Inline add */}
      <form onSubmit={addSubtask} className="flex items-center gap-2 mt-2">
        <Plus size={16} className="text-slate-300 flex-shrink-0" />
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Add a sub-task..."
          className="flex-1 text-sm bg-transparent border-none outline-none text-slate-700 placeholder:text-slate-300 py-1"
          data-testid="subtask-add-input"
        />
        {newTitle.trim() && (
          <button
            type="submit"
            disabled={adding}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 px-2 py-1"
            data-testid="subtask-add-btn"
          >
            {adding ? '...' : 'Add'}
          </button>
        )}
      </form>

      {/* Template modals */}
      {showSaveTemplate && (
        <SaveTemplateModal
          subtasks={subtasks}
          taskId={taskId}
          onClose={() => setShowSaveTemplate(false)}
          onSaved={() => {}}
        />
      )}
      {showTemplatePicker && (
        <TemplatePickerModal
          taskId={taskId}
          onClose={() => setShowTemplatePicker(false)}
          onApplied={handleTemplateApplied}
        />
      )}
      {showCopyFrom && (
        <CopyFromTaskModal
          taskId={taskId}
          onClose={() => setShowCopyFrom(false)}
          onCopied={handleCopied}
        />
      )}
    </div>
  );
}

function TaskDetailModal({ task, onClose, onUpdate, onDelete, user }) {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSaveWorkflow, setShowSaveWorkflow] = useState(false);
  const [projects, setProjects] = useState([]);
  const [filterActionType, setFilterActionType] = useState(null);

  const isTeamTask = !!task.team_id;

  useEffect(() => {
    fetchHistory();
    axios.get(`${API}/tasks/projects`, { withCredentials: true })
      .then(res => setProjects(res.data)).catch(() => {});
  }, [task.task_id]);

  const fetchHistory = async (actionType = null) => {
    try {
      const params = actionType ? { action_type: actionType } : {};
      const response = await axios.get(`${API}/tasks/${task.task_id}/history`, {
        params,
        withCredentials: true
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch task history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFilterChange = (actionType) => {
    setFilterActionType(actionType);
    fetchHistory(actionType);
  };

  const handleUpdateComplete = () => {
    setShowUpdateModal(false);
    fetchHistory(filterActionType);
    if (onUpdate) onUpdate();
  };

  const handleEditSaved = () => {
    fetchHistory(filterActionType);
    if (onUpdate) onUpdate();
  };

  const handleHistoryEdited = () => {
    fetchHistory(filterActionType);
  };

  const handleSubtaskChange = () => {
    fetchHistory(filterActionType);
    if (onUpdate) onUpdate();
  };

  const toggleStatus = async () => {
    try {
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      await axios.put(
        `${API}/tasks/${task.task_id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      fetchHistory(filterActionType);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await axios.delete(`${API}/tasks/${task.task_id}`, { withCredentials: true });
        if (onDelete) onDelete(task.task_id);
        onClose();
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM d, yyyy') : 'Invalid date';
    } catch { return 'Invalid date'; }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-50 text-red-600 border-red-200';
      case 'medium': return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'low': return 'bg-slate-50 text-slate-600 border-slate-200';
      default: return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  const subtasks = task.subtasks || [];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="task-detail-modal"
        >
          {/* Header */}
          <div className="bg-white border-b border-slate-200 p-6 flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-slate-900 mb-2">
                {task.title}
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                  {task.priority}
                </span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium border ${
                  task.status === 'completed'
                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                    : 'bg-blue-50 text-blue-600 border-blue-200'
                }`}>
                  {task.status}
                </span>
                {task.project_client && (
                  <span className="rounded-full px-2.5 py-0.5 text-xs font-medium border bg-blue-50 text-blue-600 border-blue-200">
                    {task.project_client}
                  </span>
                )}
                {task.tags?.map((tag, idx) => (
                  <span key={idx} className="rounded-full px-2.5 py-0.5 text-xs font-medium border bg-slate-50 text-slate-600 border-slate-200 flex items-center gap-1">
                    <Tag size={12} weight="duotone" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 ml-4" data-testid="close-detail-modal">
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Task Details */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              {task.description && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-slate-700 mb-1">Description</p>
                  <p className="text-sm text-slate-600">{task.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {task.due_date && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                      <CalendarBlank size={14} weight="duotone" />
                      Due Date
                    </p>
                    <p className="text-sm text-slate-900">{formatDate(task.due_date)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                    <Clock size={14} weight="duotone" />
                    Created
                  </p>
                  <p className="text-sm text-slate-900">{formatDate(task.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Sub-tasks / To-Do List */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6" data-testid="subtask-section">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <ListChecks size={16} weight="duotone" className="text-blue-600" />
                To-Do List
                {subtasks.length > 0 && (
                  <span className="text-xs font-normal text-slate-400">({subtasks.filter(s => s.status === 'completed' || s.completed).length}/{subtasks.length})</span>
                )}
              </h3>
              <SubtaskChecklist
                taskId={task.task_id}
                subtasks={subtasks}
                onUpdate={handleSubtaskChange}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              <button
                onClick={() => setShowEditModal(true)}
                className="bg-navy text-white hover:bg-navy-hover transition-colors shadow-sm font-medium px-4 py-2 rounded-lg flex items-center gap-2"
                data-testid="edit-task-btn"
              >
                <PencilSimple size={18} weight="bold" />
                Edit Task
              </button>
              <button
                onClick={() => setShowUpdateModal(true)}
                className="bg-navy text-white hover:bg-blue-700 transition-colors shadow-sm font-medium px-4 py-2 rounded-lg flex items-center gap-2"
                data-testid="update-task-btn"
              >
                <Plus size={18} weight="bold" />
                Add Update
              </button>
              <button
                onClick={toggleStatus}
                className="bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors font-medium px-4 py-2 rounded-lg flex items-center gap-2"
                data-testid="toggle-status-btn"
              >
                {task.status === 'completed' ? (
                  <><Circle size={18} weight="regular" /> Reopen</>
                ) : (
                  <><CheckCircle size={18} weight="fill" /> Mark Complete</>
                )}
              </button>
              <button
                onClick={handleDelete}
                className="bg-white text-red-600 border border-red-200 hover:bg-red-50 transition-colors font-medium px-4 py-2 rounded-lg flex items-center gap-2"
                data-testid="delete-task-btn"
              >
                <Trash size={18} weight="regular" />
                Delete
              </button>
              <button
                onClick={() => setShowSaveWorkflow(true)}
                className="bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors font-medium px-4 py-2 rounded-lg flex items-center gap-2"
                data-testid="save-as-workflow-btn"
              >
                <Lightning size={18} weight="duotone" />
                Save as Template
              </button>
            </div>

            {/* Approval Section — only for team tasks */}
            {isTeamTask && (
              <TaskApproval task={task} onUpdate={onUpdate} />
            )}

            {/* History Section */}
            <div className="border-t border-slate-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <ClockCounterClockwise size={20} weight="duotone" className="text-blue-600" />
                  Activity History
                </h3>
                {history.length > 0 && (
                  <div className="relative">
                    <select
                      value={filterActionType || 'all'}
                      onChange={(e) => handleFilterChange(e.target.value === 'all' ? null : e.target.value)}
                      className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors appearance-none pr-8"
                      data-testid="history-filter"
                    >
                      <option value="all">All Activity</option>
                      <option value="task_created">Created</option>
                      <option value="note_added">Notes</option>
                      <option value="status_changed">Status Changes</option>
                      <option value="priority_changed">Priority Changes</option>
                      <option value="due_date_changed">Due Date Changes</option>
                      <option value="task_completed">Completed</option>
                      <option value="task_reopened">Reopened</option>
                      <option value="subtask_completed">Sub-task Completed</option>
                      <option value="subtask_added">Sub-task Added</option>
                    </select>
                    <FunnelSimple size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                )}
              </div>

              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-slate-500" data-testid="no-history">
                  <p className="text-sm">No activity history yet</p>
                </div>
              ) : (
                <TaskHistoryTimeline
                  history={history}
                  taskId={task.task_id}
                  onEdited={handleHistoryEdited}
                />
              )}
            </div>

            {/* Team Comments — only for team tasks */}
            {isTeamTask && user && (
              <TeamComments taskId={task.task_id} teamId={task.team_id} currentUserId={user.user_id} />
            )}
          </div>
        </motion.div>

        {/* Update Task Modal */}
        {showUpdateModal && (
          <UpdateTaskModal
            task={task}
            onClose={() => setShowUpdateModal(false)}
            onUpdateComplete={handleUpdateComplete}
          />
        )}

        {/* Edit Task Modal */}
        {showEditModal && (
          <EditTaskModal
            task={task}
            projects={projects}
            onClose={() => setShowEditModal(false)}
            onSaved={handleEditSaved}
          />
        )}

        {/* Save as Workflow Template */}
        {showSaveWorkflow && (
          <SaveWorkflowTemplateModal
            task={task}
            onClose={() => setShowSaveWorkflow(false)}
            onSaved={() => setShowSaveWorkflow(false)}
          />
        )}
      </div>
    </AnimatePresence>
  );
}

export { SubtaskProgress };
export default TaskDetailModal;
;
